import {
  Icon,
  IconName,
  IconSize,
  IconVariant,
  InfoPanel,
  InfoPanelColor,
  Popover,
  SearchControl,
  type SearchGenerics,
  Trigger,
  useAppContext,
  useDocumentTitle
} from "@quortex/front-common"
import { useMatchesParams } from "@quortex/front-common/adapters/react-location"
import { useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import urlJoin from "url-join"
import { CdnListMetricsChart } from "@/components/CdnListMetricsChart"
import { CdnTable } from "@/components/CdnTable"
import { useCdnList } from "@/queries/cdn"
import { useWorkspaceList } from "@/queries/workspaces"

const CdnList = () => {
  const { data: workspace } = useSuspenseQuery({ ...useWorkspaceList(), select: d => d.results[0] })
  const search = useSearch<SearchGenerics>()

  useDocumentTitle(`CDNs - ${workspace?.name}`)

  const { data: cdnList } = useSuspenseQuery({ ...useCdnList(), refetchInterval: 10_000 })

  const form = useForm({ defaultValues: { search: `${search.search ?? ""}` } })
  const { apps } = useAppContext()
  const { orgUuid } = useMatchesParams()

  const oneCdnInError = useMemo(
    () => cdnList.results.find(cdn => cdn.qoeStatus === "WARNING"),
    [cdnList.results]
  )

  return (
    <FormProvider data-testid="cdn-list" {...form}>
      <h2 className="typo-2xl-medium mb-lg">CDNs</h2>

      {oneCdnInError && (
        <InfoPanel
          className="mb-lg"
          color={InfoPanelColor.WARNING}
          data-testid="cdn-qoe-warning-panel"
        >
          At least one CDN is encountering QoE warnings
        </InfoPanel>
      )}

      <CdnListMetricsChart />
      <div className="mt-lg justify-between">
        <div className="flex">
          <div className="grow">
            {workspace?.originServer === "play" &&
              cdnList.count > 0 &&
              cdnList.results[0]?.playUuid && (
                <div className="radius-xs typo-sm-medium flex w-[380px] items-center justify-between border border-grey-300 bg-grey-100 p-[12px]">
                  <div className="flex">
                    Quortex Play
                    <Popover
                      description={
                        <p>
                          Quortex Play CDNs are automatically exposed to Quortex Switch in the table
                          below.
                        </p>
                      }
                      links={[
                        {
                          children: "Go to Play ",
                          external: true,
                          href: urlJoin(
                            apps?.play || "",
                            "/organizations/",
                            orgUuid || "",
                            "/play/pools/",
                            cdnList.results[0]?.poolUuid || "",
                            "/settings/?tab=cdns"
                          )
                        }
                      ]}
                      placement="top"
                      showTransitions
                      trigger="click"
                    >
                      <Trigger className="ml-2xs" />
                    </Popover>
                  </div>
                  <div className="flex items-center text-green-600">
                    <Icon
                      className="ml-2xs"
                      data-testid="play-cdn-check-icon"
                      icon={IconName.CHECK}
                      size={IconSize.SMALL}
                      type={IconVariant.SOLID}
                    />
                    <span className="ml-xs">In sync</span>
                  </div>
                </div>
              )}
          </div>
          <div>
            <SearchControl />
          </div>
        </div>
      </div>
      <div className="mt-sm">
        <CdnTable cdnList={cdnList} />
      </div>
    </FormProvider>
  )
}

export default CdnList
